/**
 * Monkey-patch Node.prototype.removeChild and Node.prototype.insertBefore
 * so React doesn't crash when Google Translate (or other DOM-mutating
 * extensions) swaps text nodes mid-render.
 *
 * Background:
 *   When Chrome's "Translate page" is enabled, Google Translate wraps every
 *   text node in <font> elements and reparents them. React keeps a reference
 *   to the *original* DOM nodes from its virtual-DOM tree; on the next render
 *   it tries to remove/insert those nodes from a parent that no longer owns
 *   them, throwing:
 *     NotFoundError: Failed to execute 'removeChild' on 'Node':
 *     The node to be removed is not a child of this node.
 *
 *   The standard mitigation (documented in https://github.com/facebook/react/issues/11538)
 *   is to make these DOM mutations no-op + warn when the parent mismatch
 *   happens, instead of throwing. React's next reconciliation cycle will
 *   correct the tree.
 *
 * This patch is intentionally minimal and only triggers in the broken state —
 * normal DOM mutations are unaffected.
 */
export function installTranslateSafeDomPatches() {
  if (typeof Node !== 'function' || !Node.prototype) return;
  if (window.__translateSafePatched) return;
  window.__translateSafePatched = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function safeRemoveChild(child) {
    if (child && child.parentNode !== this) {
      if (typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn(
          '[translateSafe] Cannot removeChild: node is not a child of this parent. ' +
          'Likely cause: Google Translate moved the node. Skipping to avoid React crash.',
        );
      }
      // Return the node so the calling code doesn't break either.
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function safeInsertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn(
          '[translateSafe] Cannot insertBefore: reference node is not a child of this parent. ' +
          'Likely cause: Google Translate moved the node. Falling back to appendChild.',
        );
      }
      // Fallback to appendChild so the new node still ends up in the DOM.
      if (newNode) return this.appendChild(newNode);
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
}
